import { DialogTitle } from '@mui/material';
import { useEffect, useState, useMemo } from 'react';

import Dialog from '../Dialog';
import ExitConfirmationModal from '../ExitConfirmationModal';
import { useWelcomeQuery } from '../../api/getWelcomeDetails';
import useAnalytics from '../../hooks/useAnalytics';
import { EXIT_MODAL } from '../../constants/clearspeedModals';

type ClearspeedDialogProps = {
  isOpen: boolean;
  onClose: () => void;
};

const clearspeedBaseUrl = import.meta.env.VITE_CLEARSPEED_BASE_URL;
const questionnaireId = import.meta.env
  .VITE_CLEARSPEED_REINSTATEMENT_REQUEST_QUESTIONNAIRE_ID;

const ClearspeedModal = ({ isOpen, onClose }: ClearspeedDialogProps) => {
  const [showExitConfirmation, setShowExitConfirmation] = useState(false);
  const [isQuestionnaireCompleted, setIsQuestionnaireCompleted] =
    useState(false);
  const { data: welcomeData } = useWelcomeQuery();
  const questionnaire = 'Cancelled Policy: Make Payment';
  const { track } = useAnalytics();

  const iframeSrc = useMemo(() => {
    if (welcomeData?.mdmId) {
      return `${clearspeedBaseUrl}/?embedded=true&questionnaire_id=${questionnaireId}&reference_number=${welcomeData.mdmId}-${Date.now()}`;
    }
  }, [welcomeData?.mdmId]);

  useEffect(() => {
    if (isOpen) {
      track('Questionnaire Displayed', {
        event_type: 'Messaged',
        questionnaire_type: 'Risk Assessment',
        vendor: 'Clearspeed',
        questionnaire,
        action: 'First Clearspeed modal presented in questionnaire experience',
      });
    }
  }, [isOpen]);

  const trackModalClosed = (
    reason: string | undefined,
    modalName: string,
    modalMessage: string
  ) => {
    track('Modal Closed', {
      event_type: 'Option Selected',
      selection: reason,
      modal_name: modalName,
      questionnaire_type: 'Risk Assessment',
      vendor: 'Clearspeed',
      questionnaire,
      message: modalMessage,
    });
  };

  useEffect(() => {
    const messageCallback = (event: MessageEvent) => {
      const { guide_questionnaire_status, payload } = event.data || {};

      switch (guide_questionnaire_status) {
        case 'START':
          track('Questionnaire Initiated', {
            event_type: 'Link Accessed',
            questionnaire_type: 'Risk Assessment',
            vendor: 'Clearspeed',
            questionnaire,
          });
          break;
        case 'ERROR':
          if (payload) {
            track('Technical Error Encountered', {
              event_type: 'Automated System Process',
              reason: payload.error_message,
              error_code: `Clearspeed - ${payload.error_code}`,
            });
            if (
              [
                'NotAllowedError',
                'PermissionDeniedError',
                'UserPermissionError',
              ].includes(payload.error_code)
            ) {
              track('Questionnaire Declined', {
                event_type: 'Option Selected',
                selection: 'Microphone Permissions Blocked',
                questionnaire_type: 'Risk Assessment',
                vendor: 'Clearspeed',
                questionnaire,
                message: '[ User Presented Browser Permissions ]',
              });
            }
          }
          onClose();
          break;
        case 'COMPLETE':
          setIsQuestionnaireCompleted(true);
          track('Questionnaire Completed', {
            event_type: 'Automated System Process',
            questionnaire_type: 'Risk Assessment',
            vendor: 'Clearspeed',
            questionnaire,
          });
          break;
        case 'DONE':
          onClose();
          break;
        case 'CANCEL':
          setShowExitConfirmation(true);
          break;
        default:
      }
    };

    window.addEventListener('message', messageCallback);

    return () => {
      window.removeEventListener('message', messageCallback);
    };
  }, [onClose]);

  const handleClose = () => {
    if (showExitConfirmation || isQuestionnaireCompleted) {
      onClose();
      setShowExitConfirmation(false);
    } else {
      setShowExitConfirmation(true);
    }
  };

  const handleExitModalClose = (reason: string | undefined) => {
    trackModalClosed(reason, EXIT_MODAL.title, EXIT_MODAL.content);
    setShowExitConfirmation(false);
  };

  const handleExitModalContinue = async () => {
    track('Questionnaire Declined', {
      event_type: 'Option Selected',
      selection: 'Yes, leave',
      modal_name: EXIT_MODAL.title,
      questionnaire_type: 'Risk Assessment',
      vendor: 'Clearspeed',
      questionnaire,
      message: EXIT_MODAL.content,
    });
    setShowExitConfirmation(false);
    onClose();
  };

  return (
    <>
      <Dialog
        open={isOpen}
        closeDialog={handleClose}
        title={
          <DialogTitle
            sx={{
              position: 'absolute',
              left: '-10000px',
              width: '1px',
              height: '1px',
              overflow: 'hidden',
            }}
          >
            Clearspeed Questionnaire
          </DialogTitle>
        }
        fullScreenMobile
        showCloseButton={true}
        sx={{
          '& .MuiDialog-paper': {
            margin: 0,
            width: '500px',
            height: '700px',
            maxWidth: 'none',
            maxHeight: 'none',
          },
          '& .MuiDialogContent-root': {
            padding: 0,
            overflow: 'hidden',
          },
        }}
      >
        <iframe
          src={iframeSrc}
          key={iframeSrc}
          id="guideIframe"
          allow="microphone; camera; geolocation; display-capture; fullscreen; autoplay"
          allowFullScreen
          style={{
            width: '100%',
            height: '100%',
            border: 'none',
            borderRadius: 0,
          }}
        />
      </Dialog>
      <ExitConfirmationModal
        open={showExitConfirmation}
        onClose={(reason) => handleExitModalClose(reason)}
        handleContinue={handleExitModalContinue}
        title={EXIT_MODAL.title}
        bodyText={[EXIT_MODAL.content]}
        leaveText="Yes, leave"
        stayText="No, stay"
        showRemoveIcon={true}
      />
    </>
  );
};

export default ClearspeedModal;
